import React, { useEffect, useState } from "react";
import { Navigation, MapPin, Check, CircleDot, AlertTriangle } from "lucide-react";
import { Button, Input, StatusBadge, toast } from "@/components/ds";
import { api, formatApiError, type Appointment, type EtaConfig, type EtaNotification } from "@/lib/api";

const STEPS = [
  { key: "assigned", label: "Assigned", icon: CircleDot },
  { key: "en_route", label: "En route", icon: Navigation },
  { key: "arrived", label: "Arrived", icon: MapPin },
  { key: "completed", label: "Completed", icon: Check },
];
const NOTE_TONE: Record<string, "green" | "amber" | "slate"> = { sent: "green", unavailable: "amber", skipped: "slate" };

export function JobStatusPanel({ appt, onChanged }: { appt: Appointment; onChanged: () => void }) {
  const [config, setConfig] = useState<EtaConfig | null>(null);
  const [busy, setBusy] = useState(false);
  const [showWindow, setShowWindow] = useState(false);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [lastNote, setLastNote] = useState<EtaNotification | null>(null);

  useEffect(() => {
    api.etaConfig().then(setConfig).catch(() => setConfig(null));
  }, []);

  if (!config || !config.included) return null;
  const current = appt.job_status || null;
  const idx = current ? STEPS.findIndex((s) => s.key === current) : -1;

  const setStatus = async (status: string, win?: { start?: string; end?: string }) => {
    setBusy(true);
    try {
      const r = await api.etaSetStatus(appt.id, { job_status: status, eta_window_start: win?.start || null, eta_window_end: win?.end || null });
      setLastNote(r.notification);
      if (r.notification?.status === "sent") toast.success("Customer notified (on the way)");
      else if (r.notification?.status === "unavailable") toast.warning("Notification unavailable — SMS not configured");
      else toast.success(`Marked ${status.replace("_", " ")}`);
      setShowWindow(false); onChanged();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const clickStep = (key: string) => {
    if (key === "en_route" && config.eta_window) { setShowWindow(true); return; }
    setStatus(key);
  };

  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid="job-status-panel">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="flex items-center gap-1.5 text-sm font-semibold text-slate-800"><Navigation className="h-4 w-4 text-blue-600" /> Technician status & ETA</h4>
        {current && <StatusBadge tone="blue" data-testid="job-status-current">{STEPS.find((s) => s.key === current)?.label || current}</StatusBadge>}
      </div>

      <div className="flex flex-wrap gap-2" data-testid="job-status-steps">
        {STEPS.map((s, i) => {
          const done = idx >= 0 && i <= idx;
          return (
            <Button key={s.key} size="sm" variant={i === idx ? "primary" : done ? "outline" : "ghost"} disabled={busy}
              onClick={() => clickStep(s.key)} data-testid={`job-status-${s.key}`}>
              <s.icon className="h-3.5 w-3.5" /> {s.label}
            </Button>
          );
        })}
      </div>

      {showWindow && (
        <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3" data-testid="job-eta-window">
          <p className="mb-2 text-xs font-medium text-blue-800">Estimated arrival window (a manual estimate — not GPS tracking)</p>
          <div className="flex flex-wrap items-end gap-2">
            <div><p className="mb-1 text-[11px] text-slate-500">From</p><Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} className="text-xs" data-testid="job-eta-start" /></div>
            <div><p className="mb-1 text-[11px] text-slate-500">To</p><Input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} className="text-xs" data-testid="job-eta-end" /></div>
            <Button size="sm" disabled={busy} onClick={() => setStatus("en_route", { start: start ? new Date(start).toISOString() : "", end: end ? new Date(end).toISOString() : "" })} data-testid="job-eta-send">Mark en route & notify</Button>
            <Button size="sm" variant="ghost" onClick={() => setShowWindow(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {lastNote && (
        <p className="mt-2 text-xs" data-testid="job-status-notification">
          Customer notification: <StatusBadge tone={NOTE_TONE[lastNote.status] ?? "slate"}>{lastNote.status}</StatusBadge>
          {lastNote.status === "unavailable" && <span className="ml-1 text-amber-600"><AlertTriangle className="mr-0.5 inline h-3 w-3" />SMS not configured</span>}
          {lastNote.reason === "opted_out" && <span className="ml-1 text-slate-500">contact opted out</span>}
        </p>
      )}
      {!config.eta_window && <p className="mt-2 text-[11px] text-slate-400">Upgrade to Pro to add an estimated arrival window to the on-the-way message.</p>}
    </div>
  );
}
